import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useI18n } from '../../context/I18nContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Shield, ChevronLeft, ChevronRight, Loader2, User, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLogs = ({ type = 'landlord' }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState({ logs: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => { loadLogs(); }, [page]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/audit-logs/?page=${page}&limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getActionColor = (action) => {
    if (action.includes('create')) return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    if (action.includes('delete')) return 'bg-red-500/10 text-red-400 border-red-500/20';
    if (action.includes('update') || action.includes('change')) return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
  };

  return (
    <DashboardLayout type={type} title={t('audit.title')}>
      <div className="space-y-6" data-testid="audit-logs-page">
        <p className="text-muted-foreground">{t('audit.subtitle')}</p>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gold" />
          </div>
        ) : data.logs.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">{t('audit.noLogs')}</h3>
              <p className="text-muted-foreground">{t('audit.noLogsDesc')}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-2">
              {data.logs.map((log, i) => (
                <Card
                  key={log.id}
                  className="bg-card border-border card-animate"
                  style={{ animationDelay: `${i * 0.03}s` }}
                  data-testid={`audit-log-${log.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border whitespace-nowrap ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                        <span className="text-xs text-muted-foreground px-1.5 py-0.5 bg-muted/30 rounded whitespace-nowrap">
                          {log.resource_type}
                        </span>
                        <span className="text-sm truncate">{log.details || '-'}</span>
                      </div>
                      <div className="flex items-center gap-4 shrink-0 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {log.user_email}
                        </span>
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <Clock className="w-3 h-3" />
                          {formatTime(log.timestamp)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {data.pages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {page} / {data.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= data.pages}
                  onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AuditLogs;
